import { IconButton, Tooltip } from '@dixa/lib-shared-habla';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../elevio.actions';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { getUrl } from '../../utils';

/**
 * Button to manually resolve a conversation.
 *
 * It will not show if you do not have the 'elevio-suggestions' feature flag
 * enabled, or if your elevio authState is not 'fulfilled'.
 */
export function ElevioResolveButton(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const stage = useAppSelector(elevioSelectors.resolution.stage);
  const authState = useAppSelector(elevioSelectors.suggestion.authState);
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );
  const { elevioSuggestions } = useFeatureFlag(['elevio-suggestions']);

  if (authState !== 'fulfilled' || !elevioSuggestions || !languageId)
    return null; // Don't render resolve button if user does not have a KB or if the feature flag is disabled or if no languageId initialised

  const disabled =
    stage === 'solutionSubmitted' ||
    stage === 'noSolutionSubmitted' ||
    stage === 'noSolutionDraftArticleSubmitted';

  return (
    <ConditionalTooltip disabled={disabled}>
      <IconButton
        variant="secondary"
        disabled={disabled}
        icon="elevio"
        onClick={() => {
          if (conversationId) {
            dispatch(
              elevioActions.resolution.initiateResolutionPrompt({
                ticketId: conversationId.toString(),
                url: getUrl(),
                languageId,
              }),
            );
          }
        }}
      />
    </ConditionalTooltip>
  );
}

function ConditionalTooltip({
  children,
  disabled,
}: {
  children: React.ReactNode;
  disabled: boolean;
}) {
  return disabled ? (
    <>{children}</>
  ) : (
    <Tooltip content="Select Resolution...">{children}</Tooltip>
  );
}
